import React from 'react';

import { Box, Typography } from '@mui/material';
import { RepositoryUploadIcon } from 'src/components/icons/RepositoryUploadIcon';
import { makeStyles } from 'tss-react/mui';

interface RepositoryDropzoneOverlayProps {
  isDragging: boolean;
}

const useStyles = makeStyles()(theme => ({
  '@keyframes iconPulse': {
    '0%, 100%': {
      transform: 'scale(1)',
    },
    '50%': {
      transform: 'scale(1.05)',
    },
  },
  backdrop: {
    height: '100%',
    left: 0,
    pointerEvents: 'none',
    position: 'absolute',
    top: 0,
    width: '100%',
    zIndex: 1999,
  },
  iconContainer: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    gap: theme.spacing(3),
    height: 228,
    pointerEvents: 'none',
    width: 317,
  },
  iconWrapper: {
    animation: '$iconPulse 2s ease-in-out infinite',
    filter: `drop-shadow(0 4px 16px ${theme.palette.primary.main}40)`,
    height: 144,
    opacity: 0.9,
    width: 144,
  },
  overlay: {
    '& *': { pointerEvents: 'none' },
    '@supports (backdrop-filter: blur(8px))': {
      backdropFilter: 'blur(8px)',
    },
    alignItems: 'center',
    backgroundColor:
      theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.85)' : 'rgba(255, 255, 255, 0.92)',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'center',
    left: 0,
    pointerEvents: 'none',
    position: 'absolute',
    top: 0,
    transition: 'opacity 0.2s ease',
    width: '100%',
    zIndex: 2000,
  },
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: '14px',
    marginTop: theme.spacing(0.5),
    textAlign: 'center',
    width: 296,
  },
  title: {
    fontSize: '18px',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    opacity: 0.95,
    textAlign: 'center',
    width: 296,
  },
}));

export function RepositoryDropzoneOverlay({
  isDragging,
}: RepositoryDropzoneOverlayProps): JSX.Element | null {
  const { classes } = useStyles();
  if (!isDragging) return null;
  return (
    <>
      <Box className={classes.backdrop} />
      <Box aria-label="File drop zone overlay" className={classes.overlay} role="region">
        <Box className={classes.iconContainer}>
          <Box className={classes.iconWrapper}>
            <RepositoryUploadIcon />
          </Box>
          <Box>
            <Typography className={classes.title} variant="subtitle1">
              Drop files to upload
            </Typography>
            <Typography className={classes.subtitle}>
              Upload documents to your repository
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
}
